import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import TagModel from '@mm/state/model/TagModel';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { StoreState } from '@mm/state/store-interfaces';
import { trimStart } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import FilterableSelect from '../../../../../common/components/FilterableSelect/FilterableSelect';
import TextInput from '../../../../../common/components/TextInput';
import './AssignTagForm.scss';

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type OuterProps = {
  contactId: number;
  fbPageId: number;
  tags: TagModel[];
  onChange?: (tags: TagModel[]) => void;
  disabled?: boolean;
};

type Props = PropsFromState & PropsFromDispatch & OuterProps;

const AssignTagFormPure = ({
  tags,
  onTagAdd,
  availableTags,
  fbPageId,
  contactId,
  getAllTags,
  onChange,
  disabled,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQeury] = useState('');

  const onSearchQeury = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = trimStart(e.target.value);
      setSearchQeury(e.target.value);
    },
    [],
  );

  const onDropdownToggle = useCallback(
    () => setIsDropdownOpen(!isDropdownOpen),
    [isDropdownOpen],
  );

  const onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    let newTags;
    if (searchQuery) {
      newTags = await onTagAdd(searchQuery.trim(), fbPageId, contactId);
      setSearchQeury('');
    }
    onDropdownToggle();
    onChange && onChange(newTags);
  };

  const onTagSelect = useCallback(
    async (id: string, tag?: TagModel) => {
      if (disabled) return;
      let newTags;
      if (tag) {
        newTags = await onTagAdd(tag.name || '', fbPageId, contactId);
      }
      setSearchQeury('');
      if (isDropdownOpen) {
        onDropdownToggle();
      }

      onChange && onChange(newTags);
    },
    [isDropdownOpen, fbPageId, contactId],
  );

  useEffect(() => {
    if (fbPageId) {
      getAllTags(fbPageId);
    }
  }, [fbPageId]);

  useEffect(() => {
    setIsDropdownOpen(false);
    setSearchQeury('');
  }, [contactId]);

  const suggestions = useMemo(() => {
    return [
      {
        title: '',
        suggestions: tags
          ? availableTags.filter(e => !tags.some(tag => tag.name === e.name))
          : availableTags,
      },
    ];
  }, [availableTags, tags]);

  return (
    <div className="assign-tag-form">
      <form className="assign-tag-form__form" onSubmit={onSubmitForm}>
        <div className="assign-tag-form__input-wr">
          <label className="assign-tag-form__label">
            <TextInput
              className="assign-tag-form__input"
              name="name"
              value={searchQuery}
              autoComplete="false"
              readOnly
              placeholder="Add Tag"
              onFocus={onDropdownToggle}
            />
          </label>
          {isDropdownOpen && (
            <FilterableSelect
              showSuggestOnly={true}
              defaultValue={searchQuery}
              className="assign-tag-form__autocomplete"
              onBlur={onDropdownToggle}
              onInput={onSearchQeury}
              onChange={onTagSelect}
              name="filterable"
              searchPlaceholder="Add tag"
              suggestions={suggestions}
            />
          )}
        </div>
        <PrimaryButton
          type="submit"
          disabled={searchQuery.trim().length === 0 || disabled}
          className="assign-tag-form__btn"
        >
          Add
        </PrimaryButton>
      </form>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  availableTags: tagsOfKinds(state, ['regular']),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAllTags: (pageId: number) =>
    dispatch(TagsCRUDActions.getAll(pageId, true)),
  onTagAdd: (name: string, fbPageId: number, contactId: number) =>
    dispatch(TagsCRUDActions.assign(name, fbPageId, contactId)),
});

export const AssignTagForm = connect<
  PropsFromState,
  PropsFromDispatch,
  OuterProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(AssignTagFormPure);
